import axios from 'axios';
import {
  AdminLoginResponse,
  ChecklistPayload,
  Order,
  PagedResponse,
  PaymentQrResponse,
  ValidationResult,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://cij3b8xauj.execute-api.ap-south-1.amazonaws.com/Prod/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const attachAuthToken = (config: any) => {
  const token = localStorage.getItem('technicianToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const handleAuthError = (error: any) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('technicianToken');
    localStorage.removeItem('technicianUser');
    localStorage.removeItem('technicianTokenExpiresAt');
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

api.interceptors.request.use(attachAuthToken);
uploadApi.interceptors.request.use(attachAuthToken);

api.interceptors.response.use(
  (response) => response,
  handleAuthError
);

uploadApi.interceptors.response.use(
  (response) => response,
  handleAuthError
);

export const apiService = {
  async sendOtp(mobile: string): Promise<ValidationResult<void>> {
    const response = await api.post('/v1/admin-auth/send-otp', { mobile });
    return response.data;
  },

  async verifyOtp(mobile: string, otp: string): Promise<AdminLoginResponse> {
    const response = await api.post('/v1/admin-auth/verify-otp', { mobile, otp });
    return response.data;
  },

  async searchMyOrders(params: {
    page: number;
    pageSize: number;
    searchTerm?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ValidationResult<PagedResponse<Order>>> {
    const response = await api.get('/v1/admin-orders', {
      params: {
        page: 1,
        pageSize: 200,
        fromDate: params.startDate,
        toDate: params.endDate,
      },
    });

    const allOrders = Array.isArray(response.data)
      ? response.data
      : response.data?.entity || response.data?.items || [];

    const searchTerm = (params.searchTerm || '').trim().toLowerCase();
    const filteredOrders = allOrders.filter((order: Order) => {
      const matchesSearch = !searchTerm || [
        order.orderNumber,
        order.customerName,
        order.customerMobile,
        order.makeName,
        order.modelName,
      ].some((value) => (value || '').toLowerCase().includes(searchTerm));

      const matchesStatus = !params.status || order.orderStatus === params.status;
      return matchesSearch && matchesStatus;
    });

    const start = (params.page - 1) * params.pageSize;
    const items = filteredOrders.slice(start, start + params.pageSize);

    return {
      isSuccess: true,
      entity: {
        page: params.page,
        pageSize: params.pageSize,
        totalCount: filteredOrders.length,
        totalPages: Math.max(1, Math.ceil(filteredOrders.length / params.pageSize)),
        items,
      },
    };
  },

  async getOrder(id: number): Promise<ValidationResult<Order>> {
    const response = await api.get(`/v1/admin-orders/${id}`);
    return response.data;
  },

  async uploadBeforePhoto(orderId: number, photo: File, technicianId?: number) {
    const formData = new FormData();
    formData.append('photo', photo);

    const response = await uploadApi.post(`/v1/order/${orderId}/before-photo`, formData, {
      params: { technicianId },
    });
    return response.data;
  },

  async uploadAfterPhoto(orderId: number, photo: File, technicianId?: number) {
    const formData = new FormData();
    formData.append('photo', photo);

    const response = await uploadApi.post(`/v1/order/${orderId}/after-photo`, formData, {
      params: { technicianId },
    });
    return response.data;
  },

  async saveChecklist(orderId: number, checklist: ChecklistPayload) {
    const response = await api.post(`/v1/order/${orderId}/checklist`, checklist);
    return response.data;
  },

  async getPaymentQr(orderId: number): Promise<PaymentQrResponse> {
    const response = await api.get(`/v1/payment/qr/${orderId}`);
    return response.data;
  },

  async getPaymentStatus(orderId: number): Promise<string> {
    const response = await api.get(`/v1/payment/status/${orderId}`);
    if (typeof response.data === 'string') {
      return response.data;
    }
    return response.data?.status || response.data?.paymentStatus || response.data?.orderStatus || '';
  },

  async updateOrderStatus(orderId: number, status: string, userId: number, paymentMethod?: string) {
    const response = await api.put(`/v1/Order/${orderId}/status`, {
      status,
      notes: paymentMethod ? `Payment via ${paymentMethod}` : '',
      userId,
      paymentMethod,
    });
    return response.data;
  },
};
